import { useState, useEffect, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { zohoAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

function timeAgo(iso) {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function ZohoSyncPill() {
  const { user } = useAuth();
  const isPreviewEnv = user?.app_env === 'preview';
  const [status, setStatus] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await zohoAPI.getSyncStatus();
      setStatus(res.data);
    } catch {
      /* non-fatal, pill just stays hidden */
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (!status) return null;

  let colorClass = 'bg-muted text-muted-foreground';
  if (!isPreviewEnv) {
    if (status.any_failed || status.any_stale) colorClass = 'bg-red-100 text-red-700';
    else colorClass = 'bg-emerald-100 text-emerald-700';
  }

  const label = isPreviewEnv
    ? `Manual sync — last synced ${timeAgo(status.pulse_success_at)}`
    : `Zoho synced ${timeAgo(status.pulse_success_at)}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`h-8 px-2.5 rounded-sm text-xs font-medium whitespace-nowrap ${colorClass}`}
          data-testid="zoho-sync-pill"
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end" data-testid="zoho-sync-popover">
        <p className="text-sm font-semibold mb-2">Zoho Sync Status</p>
        {status.zoho_api_usage && (
          <div
            className={`flex items-center justify-between text-xs rounded-sm px-2 py-1.5 mb-2 ${
              status.zoho_api_usage.calls_today / Math.max(status.zoho_api_usage.cap, 1) > 0.9
                ? 'bg-red-100 text-red-700'
                : status.zoho_api_usage.calls_today / Math.max(status.zoho_api_usage.cap, 1) > 0.7
                ? 'bg-amber-100 text-amber-700'
                : 'bg-muted text-muted-foreground'
            }`}
            data-testid="zoho-api-usage"
          >
            <span>Zoho API calls today</span>
            <span className="font-mono font-semibold">
              {status.zoho_api_usage.calls_today.toLocaleString()} / {status.zoho_api_usage.cap.toLocaleString()}
            </span>
          </div>
        )}
        <div className="max-h-64 overflow-y-auto space-y-1.5">
          {status.entities.map((e) => (
            <div key={e.entity} className="flex items-center justify-between text-xs border-b border-border pb-1 last:border-0">
              <span className="capitalize">{e.entity.replace(/_/g, ' ')}</span>
              <span className={(e.last_status === 'failed' || e.is_stale) ? 'text-red-600' : 'text-muted-foreground'}>
                {e.last_success_at ? timeAgo(e.last_success_at) : 'never'}
                {e.last_status === 'failed' && ' (failed)'}
                {e.last_status !== 'failed' && e.is_stale && ' (overdue)'}
              </span>
            </div>
          ))}
        </div>
        {status.entities.some((e) => e.last_error) && (
          <div className="text-xs text-red-600 pt-2 mt-2 border-t border-border space-y-1" data-testid="zoho-sync-errors">
            {status.entities.filter((e) => e.last_error).map((e) => (
              <div key={e.entity}>{e.entity}: {e.last_error}</div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
